import { api, unwrap, type ApiEnvelope } from '@/lib/api';

import type {
  AddPackItemInput,
  Category,
  Hub,
  MediaAsset,
  MediaLinkInput,
  Offer,
  PackItemView,
  PackPreview,
  PaginatedProducts,
  Product,
  ProductKind,
  ProductStats,
  ProductVariant,
  ProductsListParams,
  StockEntry,
} from './types';

// ─── Hubs ──────────────────────────────────────────────────────────────────
export const hubsApi = {
  list: () => unwrap<Hub[]>(api.get<ApiEnvelope<Hub[]>>('/admin/catalog/hubs')),
  get: (id: string) => unwrap<Hub>(api.get<ApiEnvelope<Hub>>(`/admin/catalog/hubs/${id}`)),
  create: (payload: Partial<Hub>) =>
    unwrap<Hub>(api.post<ApiEnvelope<Hub>>('/admin/catalog/hubs', payload)),
  update: (id: string, payload: Partial<Hub>) =>
    unwrap<Hub>(api.patch<ApiEnvelope<Hub>>(`/admin/catalog/hubs/${id}`, payload)),
  remove: (id: string) => api.delete(`/admin/catalog/hubs/${id}`).then(() => true),
};

// ─── Categories ────────────────────────────────────────────────────────────
export interface CategoryWriteDto {
  code?: string;
  slug?: string;
  name?: string;
  parentId?: string | null;
  iconEmoji?: string | null;
  accentColor?: string | null;
  heroMediaId?: string | null;
  heroMobileMediaId?: string | null;
  heroAlt?: string | null;
  ogImageMediaId?: string | null;
  description?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  keywords?: string[];
  isActive?: boolean;
  isVisibleMobile?: boolean;
  isFeatured?: boolean;
  isSearchable?: boolean;
  sortOrder?: number;
}

export const categoriesApi = {
  list: (params: { search?: string; parentId?: string; isActive?: boolean } = {}) => {
    const usp = new URLSearchParams();
    if (params.search) usp.set('search', params.search);
    if (params.parentId) usp.set('parentId', params.parentId);
    if (params.isActive !== undefined) usp.set('isActive', String(params.isActive));
    const qs = usp.toString();
    return unwrap<Category[]>(
      api.get<ApiEnvelope<Category[]>>(`/admin/catalog/categories${qs ? '?' + qs : ''}`),
    );
  },
  tree: (archived = false) =>
    unwrap<Category[]>(
      api.get<ApiEnvelope<Category[]>>(
        `/admin/catalog/categories?tree=true${archived ? '&archived=true' : ''}`,
      ),
    ),
  get: (id: string) =>
    unwrap<Category>(api.get<ApiEnvelope<Category>>(`/admin/catalog/categories/${id}`)),
  create: (payload: CategoryWriteDto) =>
    unwrap<Category>(api.post<ApiEnvelope<Category>>('/admin/catalog/categories', payload)),
  update: (id: string, payload: CategoryWriteDto) =>
    unwrap<Category>(api.patch<ApiEnvelope<Category>>(`/admin/catalog/categories/${id}`, payload)),
  remove: (id: string) => api.delete(`/admin/catalog/categories/${id}`).then(() => true),
  archive: (id: string) =>
    unwrap<Category>(api.post<ApiEnvelope<Category>>(`/admin/catalog/categories/${id}/archive`)),
  unarchive: (id: string) =>
    unwrap<Category>(api.post<ApiEnvelope<Category>>(`/admin/catalog/categories/${id}/unarchive`)),
  reassignDelete: (id: string, targetCategoryId: string) =>
    unwrap<{ moved: number }>(
      api.post<ApiEnvelope<{ moved: number }>>(`/admin/catalog/categories/${id}/reassign-delete`, {
        targetCategoryId,
      }),
    ),
  deleteWithProducts: (id: string) =>
    unwrap<{ deletedProducts: number }>(
      api.post<ApiEnvelope<{ deletedProducts: number }>>(
        `/admin/catalog/categories/${id}/delete-with-products`,
      ),
    ),
};

// ─── Brands (référentiel T09-9.2) ────────────────────────────────────────────
export interface BrandDto {
  id: string;
  name: string;
  slug: string;
  normalized: string;
  logoMediaId: string | null;
  logoUrl: string | null;
  description: string | null;
  isActive: boolean;
  productsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BrandWriteDto {
  name?: string;
  description?: string | null;
  logoMediaId?: string | null;
  isActive?: boolean;
}

export const brandsApi = {
  list: (params: { search?: string; isActive?: boolean } = {}) => {
    const usp = new URLSearchParams();
    if (params.search) usp.set('search', params.search);
    if (params.isActive !== undefined) usp.set('isActive', String(params.isActive));
    const qs = usp.toString();
    return unwrap<BrandDto[]>(
      api.get<ApiEnvelope<BrandDto[]>>(`/admin/catalog/brands${qs ? '?' + qs : ''}`),
    );
  },
  get: (id: string) =>
    unwrap<BrandDto>(api.get<ApiEnvelope<BrandDto>>(`/admin/catalog/brands/${id}`)),
  create: (payload: BrandWriteDto) =>
    unwrap<BrandDto>(api.post<ApiEnvelope<BrandDto>>('/admin/catalog/brands', payload)),
  update: (id: string, payload: BrandWriteDto) =>
    unwrap<BrandDto>(api.patch<ApiEnvelope<BrandDto>>(`/admin/catalog/brands/${id}`, payload)),
  remove: (id: string) => api.delete(`/admin/catalog/brands/${id}`).then(() => true),
  merge: (sourceIds: string[], targetId: string) =>
    unwrap<{ moved: number; mergedFrom: number }>(
      api.post<ApiEnvelope<{ moved: number; mergedFrom: number }>>('/admin/catalog/brands/merge', {
        sourceIds,
        targetId,
      }),
    ),
};

// ─── Attributes (caractéristiques structurées T09-9.1) ───────────────────────
export type AttributeType = 'select' | 'text' | 'number' | 'boolean';

export interface AttributeValueDto {
  id: string;
  value: string;
  sortOrder: number;
}

export interface AttributeDto {
  id: string;
  code: string;
  label: string;
  type: AttributeType;
  unit: string | null;
  isActive: boolean;
  values: AttributeValueDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CategoryAttributeDto {
  attribute: AttributeDto;
  isRequired: boolean;
  sortOrder: number;
  inheritedFrom: string | null;
}

export interface AttributeWriteDto {
  code?: string;
  label?: string;
  type?: AttributeType;
  unit?: string | null;
  isActive?: boolean;
  values?: string[];
}

export interface CategoryAttributeAssignment {
  attributeId: string;
  isRequired?: boolean;
  sortOrder?: number;
}

export const attributesApi = {
  list: () =>
    unwrap<AttributeDto[]>(api.get<ApiEnvelope<AttributeDto[]>>('/admin/catalog/attributes')),
  create: (payload: AttributeWriteDto) =>
    unwrap<AttributeDto>(api.post<ApiEnvelope<AttributeDto>>('/admin/catalog/attributes', payload)),
  update: (id: string, payload: AttributeWriteDto) =>
    unwrap<AttributeDto>(
      api.patch<ApiEnvelope<AttributeDto>>(`/admin/catalog/attributes/${id}`, payload),
    ),
  remove: (id: string) => api.delete(`/admin/catalog/attributes/${id}`).then(() => true),
  forCategory: (categoryId: string) =>
    unwrap<CategoryAttributeDto[]>(
      api.get<ApiEnvelope<CategoryAttributeDto[]>>(
        `/admin/catalog/attributes/category/${categoryId}`,
      ),
    ),
  setForCategory: (categoryId: string, assignments: CategoryAttributeAssignment[]) =>
    unwrap<CategoryAttributeDto[]>(
      api.put<ApiEnvelope<CategoryAttributeDto[]>>(
        `/admin/catalog/attributes/category/${categoryId}`,
        {
          assignments,
        },
      ),
    ),
};

// ─── Products ──────────────────────────────────────────────────────────────
export interface ProductWriteDto {
  sku?: string;
  slug?: string;
  title?: string;
  kind?: ProductKind;
  brand?: string | null;
  brandId?: string | null;
  categoryId?: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  currency?: string;
  basePrice?: number;
  compareAtPrice?: number | null;
  attributes?: Record<string, unknown>;
  tags?: string[];
  metaTitle?: string | null;
  metaDescription?: string | null;
  mediaLinks?: MediaLinkInput[];
}

export const productsApi = {
  stats: () =>
    unwrap<ProductStats>(api.get<ApiEnvelope<ProductStats>>('/admin/catalog/products/stats')),
  list: (params: ProductsListParams = {}) => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
    });
    const qs = usp.toString();
    return unwrap<PaginatedProducts>(
      api.get<ApiEnvelope<PaginatedProducts>>(`/admin/catalog/products${qs ? '?' + qs : ''}`),
    );
  },
  get: (id: string) =>
    unwrap<Product>(api.get<ApiEnvelope<Product>>(`/admin/catalog/products/${id}`)),
  create: (payload: ProductWriteDto) =>
    unwrap<Product>(api.post<ApiEnvelope<Product>>('/admin/catalog/products', payload)),
  update: (id: string, payload: ProductWriteDto) =>
    unwrap<Product>(api.patch<ApiEnvelope<Product>>(`/admin/catalog/products/${id}`, payload)),
  remove: (id: string) => api.delete(`/admin/catalog/products/${id}`).then(() => true),
  publish: (id: string) =>
    unwrap<Product>(api.post<ApiEnvelope<Product>>(`/admin/catalog/products/${id}/publish`)),
  unpublish: (id: string) =>
    unwrap<Product>(api.post<ApiEnvelope<Product>>(`/admin/catalog/products/${id}/unpublish`)),
  archive: (id: string) =>
    unwrap<Product>(api.post<ApiEnvelope<Product>>(`/admin/catalog/products/${id}/archive`)),
  enhanceImages: (id: string) =>
    unwrap<Product>(api.post<ApiEnvelope<Product>>(`/admin/catalog/products/${id}/enhance-images`)),
  enhanceOneImage: (id: string, mediaId: string) =>
    unwrap<Product>(
      api.post<ApiEnvelope<Product>>(
        `/admin/catalog/products/${id}/media/${mediaId}/enhance`,
      ),
    ),
  removeMedia: (id: string, mediaId: string) =>
    unwrap<Product>(
      api.delete<ApiEnvelope<Product>>(`/admin/catalog/products/${id}/media/${mediaId}`),
    ),
  /** Aperçu du passage en produit simple — n'écrit rien. */
  previewConvertToSimple: (id: string, keepVariantId?: string) =>
    unwrap<SimpleConversionPreview>(
      api.get<ApiEnvelope<SimpleConversionPreview>>(
        `/admin/catalog/products/${id}/convert-to-simple/preview${
          keepVariantId ? `?keepVariantId=${keepVariantId}` : ''
        }`,
      ),
    ),
  convertToSimple: (id: string, keepVariantId: string) =>
    unwrap<Product>(
      api.post<ApiEnvelope<Product>>(`/admin/catalog/products/${id}/convert-to-simple`, {
        keepVariantId,
      }),
    ),
};

interface VariantRef {
  id: string;
  name: string;
  sku: string;
}

/** Ce que ferait le passage en produit simple (voir l'API). */
export interface SimpleConversionPreview {
  variantes: VariantRef[];
  gardee: VariantRef;
  stockParHub: Array<{ hubId: string; hubName: string | null; avant: number; apres: number }>;
  stockTotal: number;
  /** Sans historique : supprimées. */
  supprimees: VariantRef[];
  /** Citées par une commande : gardées en base, désactivées. */
  desactivees: VariantRef[];
  /** Vide = la conversion peut avoir lieu. */
  blocages: string[];
}

// ─── Variants ──────────────────────────────────────────────────────────────
export interface VariantWriteDto {
  sku?: string;
  name?: string;
  optionValues?: Record<string, string>;
  priceOverride?: number | null;
  compareAtPriceOverride?: number | null;
  costPrice?: number | null;
  weightKg?: number | null;
  heightCm?: number | null;
  widthCm?: number | null;
  depthCm?: number | null;
  barcode?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  mediaLinks?: MediaLinkInput[];
}

/** P18 — composition des packs (admin/catalog/packs). */
export const packsApi = {
  preview: (productId: string) =>
    unwrap<PackPreview>(
      api.get<ApiEnvelope<PackPreview>>(`/admin/catalog/products/${productId}/pack-preview`),
    ),
  addItem: (productId: string, payload: AddPackItemInput) =>
    unwrap<PackItemView>(
      api.post<ApiEnvelope<PackItemView>>(
        `/admin/catalog/products/${productId}/pack-items`,
        payload,
      ),
    ),
  updateItem: (itemId: string, payload: Partial<Omit<AddPackItemInput, 'componentVariantId'>>) =>
    unwrap<PackItemView>(
      api.patch<ApiEnvelope<PackItemView>>(`/admin/catalog/pack-items/${itemId}`, payload),
    ),
  removeItem: (itemId: string) =>
    api.delete(`/admin/catalog/pack-items/${itemId}`).then(() => true),
  replaceItems: (productId: string, items: AddPackItemInput[]) =>
    unwrap<PackPreview>(
      api.put<ApiEnvelope<PackPreview>>(`/admin/catalog/products/${productId}/pack-items`, {
        items,
      }),
    ),
};

export const variantsApi = {
  listForProduct: (productId: string) =>
    unwrap<ProductVariant[]>(
      api.get<ApiEnvelope<ProductVariant[]>>(`/admin/catalog/products/${productId}/variants`),
    ),
  create: (productId: string, payload: VariantWriteDto) =>
    unwrap<ProductVariant>(
      api.post<ApiEnvelope<ProductVariant>>(
        `/admin/catalog/products/${productId}/variants`,
        payload,
      ),
    ),
  update: (id: string, payload: VariantWriteDto) =>
    unwrap<ProductVariant>(
      api.patch<ApiEnvelope<ProductVariant>>(`/admin/catalog/variants/${id}`, payload),
    ),
  /** `force` vide le stock restant et met les offres en pause avant de supprimer. */
  remove: (id: string, force = false) =>
    api.delete(`/admin/catalog/variants/${id}${force ? '?force=true' : ''}`).then(() => true),
};

// ─── Offers ────────────────────────────────────────────────────────────────
export interface OfferWriteDto {
  condition?: 'new' | 'refurbished' | 'used' | 'open_box';
  status?: 'active' | 'paused' | 'expired';
  price?: number;
  compareAtPrice?: number | null;
  currency?: string;
  preferredHubId?: string | null;
  minOrderQty?: number;
  maxOrderQty?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  notes?: string | null;
}

export const offersApi = {
  listForVariant: (variantId: string) =>
    unwrap<Offer[]>(api.get<ApiEnvelope<Offer[]>>(`/admin/catalog/variants/${variantId}/offers`)),
  create: (variantId: string, payload: OfferWriteDto & { price: number }) =>
    unwrap<Offer>(
      api.post<ApiEnvelope<Offer>>(`/admin/catalog/variants/${variantId}/offers`, payload),
    ),
  update: (id: string, payload: OfferWriteDto) =>
    unwrap<Offer>(api.patch<ApiEnvelope<Offer>>(`/admin/catalog/offers/${id}`, payload)),
  remove: (id: string) => api.delete(`/admin/catalog/offers/${id}`).then(() => true),
};

// ─── Stock ─────────────────────────────────────────────────────────────────
export interface SetStockPayload {
  variantId: string;
  hubId: string;
  quantity: number;
  reservedQuantity?: number;
  safetyStock?: number;
  reorderPoint?: number;
}

export const stockApi = {
  list: (params: { variantId?: string; hubId?: string } = {}) => {
    const usp = new URLSearchParams();
    if (params.variantId) usp.set('variantId', params.variantId);
    if (params.hubId) usp.set('hubId', params.hubId);
    const qs = usp.toString();
    return unwrap<StockEntry[]>(
      api.get<ApiEnvelope<StockEntry[]>>(`/admin/catalog/stock${qs ? '?' + qs : ''}`),
    );
  },
  set: (payload: SetStockPayload) =>
    unwrap<StockEntry>(api.post<ApiEnvelope<StockEntry>>('/admin/catalog/stock', payload)),
  adjust: (payload: { variantId: string; hubId: string; delta: number; reason?: string }) =>
    unwrap<StockEntry>(api.post<ApiEnvelope<StockEntry>>('/admin/catalog/stock/adjust', payload)),
};

// ─── Import / Export de masse (T09-9.3) ──────────────────────────────────────
export interface ImportPreviewRow {
  line: number;
  data: Record<string, string>;
  action: 'create' | 'update';
  errors: string[];
}
export interface ImportPreview {
  rows: ImportPreviewRow[];
  summary: { total: number; toCreate: number; toUpdate: number; invalid: number };
}
export interface ImportJobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total: number;
  processed: number;
  created: number;
  updated: number;
  failed: number;
  errors: { line: number; message: string }[];
  createdAt: string;
  updatedAt: string;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const importExportApi = {
  downloadTemplate: async () => {
    const res = await api.get('/admin/catalog/import/template', { responseType: 'blob' });
    triggerDownload(res.data as Blob, 'modele-import-akwaba.xlsx');
  },
  downloadExport: async (
    filters: {
      search?: string;
      status?: string;
      categoryId?: string;
      hubId?: string;
      brandId?: string;
    } = {},
  ) => {
    const usp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
    });
    const qs = usp.toString();
    const res = await api.get(`/admin/catalog/export${qs ? '?' + qs : ''}`, {
      responseType: 'blob',
    });
    triggerDownload(res.data as Blob, 'export-produits-akwaba.xlsx');
  },
  preview: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap<ImportPreview>(
      api.post<ApiEnvelope<ImportPreview>>('/admin/catalog/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },
  commit: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap<{ jobId: string; total: number; invalid: number }>(
      api.post<ApiEnvelope<{ jobId: string; total: number; invalid: number }>>(
        '/admin/catalog/import/commit',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },
  job: (id: string) =>
    unwrap<ImportJobStatus>(
      api.get<ApiEnvelope<ImportJobStatus>>(`/admin/catalog/import/jobs/${id}`),
    ),
};

// ─── Media (MinIO upload via API + médiathèque T09-9.4) ──────────────────────
export interface MediaLibraryItem extends MediaAsset {
  usageCount: number;
}
export interface MediaListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  kind?: 'image' | 'video' | 'document';
  usage?: 'used' | 'unused';
}
export interface PaginatedMedia {
  items: MediaLibraryItem[];
  total: number;
  page: number;
  pageSize: number;
}
export interface MissingVisualProduct {
  id: string;
  sku: string;
  title: string;
  status: string;
  categoryId: string;
}
export interface PaginatedMissingVisual {
  items: MissingVisualProduct[];
  total: number;
  page: number;
  pageSize: number;
}

export const mediaApi = {
  list: (params: MediaListParams = {}) => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
    });
    const qs = usp.toString();
    return unwrap<PaginatedMedia>(
      api.get<ApiEnvelope<PaginatedMedia>>(`/admin/catalog/media${qs ? '?' + qs : ''}`),
    );
  },
  missingVisual: (params: { page?: number; pageSize?: number; search?: string } = {}) => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
    });
    const qs = usp.toString();
    return unwrap<PaginatedMissingVisual>(
      api.get<ApiEnvelope<PaginatedMissingVisual>>(
        `/admin/catalog/media/missing-visual${qs ? '?' + qs : ''}`,
      ),
    );
  },
  upload: async (file: File, opts: { altText?: string } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.altText) fd.append('altText', opts.altText);
    return unwrap<MediaAsset>(
      api.post<ApiEnvelope<MediaAsset>>('/admin/catalog/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },
  get: (id: string) =>
    unwrap<MediaAsset>(api.get<ApiEnvelope<MediaAsset>>(`/admin/catalog/media/${id}`)),
  update: (id: string, payload: { altText?: string }) =>
    unwrap<MediaAsset>(api.patch<ApiEnvelope<MediaAsset>>(`/admin/catalog/media/${id}`, payload)),
  remove: (id: string) => api.delete(`/admin/catalog/media/${id}`).then(() => true),
};
